import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { useDateLocale } from '@/hooks/useDateLocale';
import { Loader2, Calendar, FileSpreadsheet, Download, Printer, ChevronsUpDown, Check } from 'lucide-react';
import { format, startOfDay, endOfDay, subDays, formatDuration, eachDayOfInterval } from 'date-fns';
import {
  ResponsiveContainer, ComposedChart, BarChart, Bar, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  Cell,
} from 'recharts';
import { DateRange } from 'react-day-picker';
import * as XLSX from 'xlsx';
import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import { useTraccarSession } from '@/hooks/useTraccarSession';
import { useBranding } from '@/hooks/useBranding';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  fetchDevices,
  fetchDrivers,
  fetchTrips,
  fetchSummary,
  fetchStops,
  fetchEvents,
  fetchRoute,
  fetchPositionsByIds,
  type Device,
  type Driver,
  type Trip,
  type Summary,
  type Stop,
  type Event,
  type Position,
} from '@/lib/traccar-api';

type ReportType = 'trips' | 'events' | 'stops' | 'summary' | 'route' | 'raw' | 'driver' | 'speeding' | 'ranking' | 'activity' | 'events-chart' | 'driver-chart' | 'idle';

const CHART_COLORS = ['#1a56db', '#7c3aed', '#059669', '#d97706', '#dc2626', '#0891b2', '#65a30d', '#db2777'];

interface DriverSummary {
  driverUniqueId: string;
  driverName: string;
  trips: number;
  distance: number;
  duration: number;
  averageSpeed: number;
  maxSpeed: number;
}

interface SpeedingEvent {
  deviceName: string;
  driverName: string;
  startTime: string;
  endTime: string;
  duration: number;
  maxSpeed: number;
  avgSpeed: number;
  address: string;
  latitude: number;
  longitude: number;
  fixTime: string;
  startSpeed: number;
}

interface RankedDriver {
  rank: number;
  driverUniqueId: string;
  driverName: string;
  trips: number;
  distance: number;
  duration: number;
  averageSpeed: number; // knots
  maxSpeed: number;     // knots
  harshBraking: number;
  harshAcceleration: number;
  harshCornering: number;
  score: number;        // 0–100
}

// Score: 40 pts avg speed (full at ≤70 km/h, zero at ≥130 km/h)
//      + 40 pts max speed (full at ≤90 km/h, zero at ≥180 km/h)
//      + 20 pts harsh events (20 - harshCount*2, min 0)
const computeScore = (avgKnots: number, maxKnots: number, harshCount: number): number => {
  const avg = avgKnots * 1.852;
  const max = maxKnots * 1.852;
  const avgPts = Math.max(0, 40 - Math.max(0, avg - 70) * (40 / 60));
  const maxPts = Math.max(0, 40 - Math.max(0, max - 90) * (40 / 90));
  const harshPts = Math.max(0, 20 - harshCount * 2);
  return Math.round(avgPts + maxPts + harshPts);
};

function TwoLineAddress({ address }: { address: string | undefined | null }) {
  if (!address) return <span>—</span>;
  const comma = address.indexOf(',');
  if (comma === -1) return <span>{address}</span>;
  return (
    <span>
      {address.slice(0, comma)}
      <br />
      <span className="text-muted-foreground text-xs">{address.slice(comma + 1).trim()}</span>
    </span>
  );
}

function resolveDriverName(
  eventTime: number,
  devicePositions: Position[],
  firstPosDriverUniqueId: string | undefined,
  drivers: Driver[]
): string {
  let driverUniqueId = firstPosDriverUniqueId;

  if (!driverUniqueId?.trim()) {
    let ignitionOnTime: number | undefined;
    for (let i = devicePositions.length - 1; i >= 0; i--) {
      if (new Date(devicePositions[i].fixTime).getTime() > eventTime) continue;
      if (devicePositions[i].attributes?.ignition === true && !devicePositions[i - 1]?.attributes?.ignition) {
        ignitionOnTime = new Date(devicePositions[i].fixTime).getTime();
        break;
      }
    }

    let ignitionOffTime: number | undefined;
    for (const p of devicePositions) {
      if (new Date(p.fixTime).getTime() <= eventTime) continue;
      if (!p.attributes?.ignition) {
        ignitionOffTime = new Date(p.fixTime).getTime();
        break;
      }
    }

    if (ignitionOnTime !== undefined || ignitionOffTime !== undefined) {
      const found = devicePositions.find(p =>
        (ignitionOnTime === undefined || new Date(p.fixTime).getTime() >= ignitionOnTime) &&
        (ignitionOffTime === undefined || new Date(p.fixTime).getTime() <= ignitionOffTime) &&
        (p.attributes?.driverUniqueId as string | undefined)?.trim()
      );
      driverUniqueId = found?.attributes?.driverUniqueId as string | undefined;
    }
  }

  return drivers.find(d => d.uniqueId === driverUniqueId)?.name || driverUniqueId || '';
}

const Reports = () => {
  const { t } = useTranslation();
  const dateFnsLocale = useDateLocale();
  const { isAuthenticated } = useTraccarSession();
  const { title: brandTitle, favicon: brandLogo } = useBranding();
  const [searchParams] = useSearchParams();

  const [devices, setDevices] = useState<Device[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [reportType, setReportType] = useState<ReportType>(() => (searchParams.get('type') as ReportType) || 'trips');
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>(() => searchParams.get('deviceId') || 'all');
  const [devicePickerOpen, setDevicePickerOpen] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState<string>('all');
  const [speedLimit, setSpeedLimit] = useState<number>(90);
  const [minIdleTime, setMinIdleTime] = useState<number>(10);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      const d = new Date(dateParam);
      return { from: startOfDay(d), to: endOfDay(d) };
    }
    return { from: startOfDay(subDays(new Date(), 7)), to: endOfDay(new Date()) };
  });

  // Report data states
  const [trips, setTrips] = useState<Trip[]>([]);
  const [summaries, setSummaries] = useState<Summary[]>([]);
  const [stops, setStops] = useState<Stop[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [eventTypeFilter, setEventTypeFilter] = useState<Set<string>>(new Set());
  const [eventPositions, setEventPositions] = useState<Map<number, Position>>(new Map());
  const [positions, setPositions] = useState<Position[]>([]);
  const [rawPage, setRawPage] = useState(0);
  const RAW_PAGE_SIZE = 500;
  const [driverTrips, setDriverTrips] = useState<DriverSummary[]>([]);
  const [speedingEvents, setSpeedingEvents] = useState<SpeedingEvent[]>([]);
  const [rankingData, setRankingData] = useState<RankedDriver[]>([]);
  const [hasGenerated, setHasGenerated] = useState(false);

  const deviceIdParam = searchParams.get('deviceId');
  const loadInitialData = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setIsLoading(true);
      const [devicesData, driversData] = await Promise.all([fetchDevices(), fetchDrivers()]);
      setDevices(devicesData);
      setDrivers(driversData);
      if (devicesData.length > 0) {
        const valid = deviceIdParam && devicesData.some(d => d.id.toString() === deviceIdParam);
        setSelectedDeviceId(valid ? deviceIdParam : devicesData[0].id.toString());
      }
      setHasLoaded(true);
    } catch (error) {
      console.error('Failed to load initial data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, deviceIdParam]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const formatHours = useCallback((ms: number) => {
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    return formatDuration({ hours, minutes }, { format: ['hours', 'minutes'], zero: false, locale: dateFnsLocale }) || '0h';
  }, [dateFnsLocale]);

  const clearResults = () => {
    setTrips([]);
    setSummaries([]);
    setStops([]);
    setEvents([]);
    setEventTypeFilter(new Set());
    setEventPositions(new Map());
    setPositions([]);
    setDriverTrips([]);
    setSpeedingEvents([]);
    setRankingData([]);
  };

  const generateReport = useCallback(async () => {
    if (!dateRange?.from) return;

    try {
      setIsGenerating(true);
      clearResults();
      const from = format(dateRange.from, "yyyy-MM-dd'T'HH:mm:ss'Z'");
      const to = format(dateRange.to ?? endOfDay(dateRange.from), "yyyy-MM-dd'T'HH:mm:ss'Z'");
      const isAll = selectedDeviceId === 'all';
      const deviceId = isAll ? NaN : parseInt(selectedDeviceId, 10);
      const deviceIds = isAll ? devices.map(d => d.id) : [deviceId];

      switch (reportType) {
        case 'trips': {
          const arrays = await Promise.all(deviceIds.map(id => fetchTrips(id, from, to).catch(() => [] as Trip[])));
          const allTrips = arrays.flat();

          const missingDriverDeviceIds = [...new Set(
            allTrips.filter(t => !t.driverUniqueId?.trim()).map(t => t.deviceId)
          )];

          if (missingDriverDeviceIds.length > 0) {
            const posArrays = await Promise.all(
              missingDriverDeviceIds.map(id => fetchRoute(id, from, to).catch(() => [] as Position[]))
            );
            const positionsByDevice = new Map<number, Position[]>();
            for (let i = 0; i < missingDriverDeviceIds.length; i++) {
              const sorted = [...posArrays[i]].sort((a, b) => new Date(a.fixTime).getTime() - new Date(b.fixTime).getTime());
              positionsByDevice.set(missingDriverDeviceIds[i], sorted);
            }

            for (const trip of allTrips) {
              if (trip.driverUniqueId?.trim()) continue;
              const devPositions = positionsByDevice.get(trip.deviceId);
              if (!devPositions?.length) continue;
              trip.driverName = resolveDriverName(
                new Date(trip.startTime).getTime(),
                devPositions,
                undefined,
                drivers
              ) || undefined;
            }
          }

          setTrips(allTrips);
          break;
        }
        case 'summary': {
          const data = await fetchSummary(deviceIds, from, to);
          setSummaries(data);
          break;
        }
        case 'stops': {
          const arrays = await Promise.all(deviceIds.map(id => fetchStops(id, from, to).catch(() => [] as Stop[])));
          setStops(arrays.flat());
          break;
        }
        case 'idle': {
          const arrays = await Promise.all(deviceIds.map(id => fetchRoute(id, from, to).catch(() => [] as Position[])));
          const minMs = minIdleTime * 60 * 1000;
          const FIVE_MIN = 5 * 60 * 1000;
          const IDLE_SPEED = 2; // km/h
          const idleStops: Stop[] = [];

          for (const positions of arrays) {
            if (positions.length === 0) continue;
            const sorted = [...positions].sort((a, b) => new Date(a.fixTime).getTime() - new Date(b.fixTime).getTime());
            const deviceId = sorted[0].deviceId;
            const deviceName = devices.find(d => d.id === deviceId)?.name || `Device ${deviceId}`;

            let idleStart: Position | null = null;
            let lastIdlePos: Position | null = null;
            let idleDuration = 0;

            for (const pos of sorted) {
              const isIdle = pos.speed < IDLE_SPEED && pos.attributes?.ignition === true;

              if (isIdle) {
                if (idleStart === null) {
                  idleStart = pos;
                  idleDuration = 0;
                } else if (lastIdlePos) {
                  const gap = new Date(pos.fixTime).getTime() - new Date(lastIdlePos.fixTime).getTime();
                  if (gap < FIVE_MIN) {
                    idleDuration += gap;
                  }
                }
                lastIdlePos = pos;
              } else {
                if (idleStart !== null && idleDuration >= minMs) {
                  const driverName = resolveDriverName(
                    new Date(idleStart.fixTime).getTime(),
                    sorted,
                    idleStart.attributes?.driverUniqueId as string | undefined,
                    drivers
                  );
                  idleStops.push({
                    deviceId,
                    deviceName,
                    driverName,
                    duration: new Date(lastIdlePos!.fixTime).getTime() - new Date(idleStart.fixTime).getTime(),
                    startTime: idleStart.fixTime,
                    endTime: lastIdlePos!.fixTime,
                    address: idleStart.address || '',
                    latitude: idleStart.latitude,
                    longitude: idleStart.longitude,
                    engineHours: idleDuration,
                  });
                }
                idleStart = null;
                lastIdlePos = null;
                idleDuration = 0;
              }
            }

            // Handle trailing idle period
            if (idleStart !== null && lastIdlePos && idleDuration >= minMs) {
              const driverName = resolveDriverName(
                new Date(idleStart.fixTime).getTime(),
                sorted,
                idleStart.attributes?.driverUniqueId as string | undefined,
                drivers
              );
              idleStops.push({
                deviceId,
                deviceName,
                driverName,
                duration: new Date(lastIdlePos.fixTime).getTime() - new Date(idleStart.fixTime).getTime(),
                startTime: idleStart.fixTime,
                endTime: lastIdlePos.fixTime,
                address: idleStart.address || '',
                latitude: idleStart.latitude,
                longitude: idleStart.longitude,
                engineHours: idleDuration,
              });
            }
          }

          setStops(idleStops);
          break;
        }
        case 'events': {
          const arrays = await Promise.all(deviceIds.map(id => fetchEvents(id, from, to).catch(() => [] as Event[])));
          const evs = arrays.flat();
          setEvents(evs);
          setEventTypeFilter(new Set());
          const posIds = [...new Set(evs.map(ev => ev.positionId).filter(Boolean))];
          const evPositions = await fetchPositionsByIds(posIds).catch(() => [] as Position[]);
          setEventPositions(new Map(evPositions.map(p => [p.id, p])));
          break;
        }
        case 'route': {
          const arrays = await Promise.all(deviceIds.map(id => fetchRoute(id, from, to).catch(() => [] as Position[])));
          setPositions(arrays.flat());
          break;
        }
        case 'raw': {
          const arrays = await Promise.all(deviceIds.map(id => fetchRoute(id, from, to).catch(() => [] as Position[])));
          setPositions(arrays.flat());
          setRawPage(0);
          break;
        }
        case 'driver': {
          const allTripsArrays = await Promise.all(
            devices.map(d => fetchTrips(d.id, from, to).catch(() => [] as Trip[]))
          );
          const allTrips = allTripsArrays.flat();
          const filtered = selectedDriverId === 'all'
            ? allTrips
            : allTrips.filter(t => t.driverUniqueId === selectedDriverId);

          const accumulated = new Map<string, { driverName: string; trips: number; distance: number; duration: number; speedSum: number; maxSpeed: number }>();
          for (const trip of filtered) {
            const key = trip.driverUniqueId || '';
            const driverName = trip.driverName ||
              drivers.find(d => d.uniqueId === trip.driverUniqueId)?.name ||
              (trip.driverUniqueId ? `Unknown (${trip.driverUniqueId})` : t('reports.noDriver'));
            if (!accumulated.has(key)) {
              accumulated.set(key, { driverName, trips: 0, distance: 0, duration: 0, speedSum: 0, maxSpeed: 0 });
            }
            const entry = accumulated.get(key)!;
            entry.trips++;
            entry.distance += trip.distance;
            entry.duration += trip.duration;
            entry.speedSum += trip.averageSpeed;
            entry.maxSpeed = Math.max(entry.maxSpeed, trip.maxSpeed);
          }

          setDriverTrips(Array.from(accumulated.entries()).map(([driverUniqueId, e]) => ({
            driverUniqueId,
            driverName: e.driverName,
            trips: e.trips,
            distance: e.distance,
            duration: e.duration,
            averageSpeed: e.trips > 0 ? e.speedSum / e.trips : 0,
            maxSpeed: e.maxSpeed,
          })));
          break;
        }
        case 'speeding': {
          const arrays = await Promise.all(deviceIds.map(id => fetchRoute(id, from, to).catch(() => [] as Position[])));
          const data = arrays.flat();
          const sorted = [...data].sort((a, b) => new Date(a.fixTime).getTime() - new Date(b.fixTime).getTime());
          const limitKnots = speedLimit / 1.852;
          const result: SpeedingEvent[] = [];
          let group: Position[] = [];

          const finalizeGroup = () => {
            if (group.length === 0) return;
            const startTime = group[0].fixTime;
            const endTime = group[group.length - 1].fixTime;
            const duration = new Date(endTime).getTime() - new Date(startTime).getTime();
            const maxSpeed = Math.max(...group.map(p => p.speed));
            const avgSpeed = group.reduce((sum, p) => sum + p.speed, 0) / group.length;
            const deviceName = devices.find(d => d.id === group[0].deviceId)?.name || `Device ${group[0].deviceId}`;
            const { latitude, longitude, fixTime, speed: startSpeed } = group[0];
            const deviceId = group[0].deviceId;
            const devPositions = sorted.filter(p => p.deviceId === deviceId);
            const driverName = resolveDriverName(
              new Date(group[0].fixTime).getTime(),
              devPositions,
              group[0].attributes?.driverUniqueId as string | undefined,
              drivers
            );
            result.push({ deviceName, driverName, startTime, endTime, duration, maxSpeed, avgSpeed, address: group[0].address || '', latitude, longitude, fixTime, startSpeed });
            group = [];
          };

          for (const pos of sorted) {
            if (pos.speed > limitKnots) {
              group.push(pos);
            } else {
              finalizeGroup();
            }
          }
          finalizeGroup();
          result.sort((a, b) => a.deviceName.localeCompare(b.deviceName) || new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
          setSpeedingEvents(result);
          break;
        }
        case 'ranking': {
          const [allTripsArrays, allEventsArrays] = await Promise.all([
            Promise.all(devices.map(d => fetchTrips(d.id, from, to).catch(() => [] as Trip[]))),
            Promise.all(devices.map(d => fetchEvents(d.id, from, to).catch(() => [] as Event[]))),
          ]);
          const allTrips = allTripsArrays.flat();
          const allEvents = allEventsArrays.flat();

          // Build trip index for harsh event attribution: deviceId → sorted trips
          const tripsByDevice = new Map<number, Trip[]>();
          for (const trip of allTrips) {
            if (!tripsByDevice.has(trip.deviceId)) tripsByDevice.set(trip.deviceId, []);
            tripsByDevice.get(trip.deviceId)!.push(trip);
          }

          // Count harsh events per driver by matching event time to trip time range
          const harshCounts = new Map<string, { braking: number; acceleration: number; cornering: number }>();
          const HARSH_ALARMS = new Set(['hardBraking', 'hardAcceleration', 'hardCornering']);
          for (const ev of allEvents) {
            if (ev.type !== 'alarm') continue;
            const alarm = ev.attributes?.alarm as string | undefined;
            if (!alarm || !HARSH_ALARMS.has(alarm)) continue;
            const evTime = new Date(ev.eventTime).getTime();
            const deviceTrips = tripsByDevice.get(ev.deviceId) || [];
            const matchingTrip = deviceTrips.find(t =>
              evTime >= new Date(t.startTime).getTime() && evTime <= new Date(t.endTime).getTime()
            );
            const driverKey = matchingTrip?.driverUniqueId || '';
            if (!harshCounts.has(driverKey)) harshCounts.set(driverKey, { braking: 0, acceleration: 0, cornering: 0 });
            const hc = harshCounts.get(driverKey)!;
            if (alarm === 'hardBraking') hc.braking++;
            else if (alarm === 'hardAcceleration') hc.acceleration++;
            else if (alarm === 'hardCornering') hc.cornering++;
          }

          const accumulated = new Map<string, { driverName: string; trips: number; distance: number; duration: number; speedSum: number; maxSpeed: number }>();
          for (const trip of allTrips) {
            const key = trip.driverUniqueId || '';
            const driverName = trip.driverName ||
              drivers.find(d => d.uniqueId === trip.driverUniqueId)?.name ||
              (trip.driverUniqueId ? `Unknown (${trip.driverUniqueId})` : t('reports.noDriver'));
            if (!accumulated.has(key)) {
              accumulated.set(key, { driverName, trips: 0, distance: 0, duration: 0, speedSum: 0, maxSpeed: 0 });
            }
            const entry = accumulated.get(key)!;
            entry.trips++;
            entry.distance += trip.distance;
            entry.duration += trip.duration;
            entry.speedSum += trip.averageSpeed;
            entry.maxSpeed = Math.max(entry.maxSpeed, trip.maxSpeed);
          }

          const ranked = Array.from(accumulated.entries())
            .map(([driverUniqueId, e]) => {
              const averageSpeed = e.trips > 0 ? e.speedSum / e.trips : 0;
              const hc = harshCounts.get(driverUniqueId) || { braking: 0, acceleration: 0, cornering: 0 };
              const harshCount = hc.braking + hc.acceleration + hc.cornering;
              return {
                rank: 0,
                driverUniqueId,
                driverName: e.driverName,
                trips: e.trips,
                distance: e.distance,
                duration: e.duration,
                averageSpeed,
                maxSpeed: e.maxSpeed,
                harshBraking: hc.braking,
                harshAcceleration: hc.acceleration,
                harshCornering: hc.cornering,
                score: computeScore(averageSpeed, e.maxSpeed, harshCount),
              };
            })
            .sort((a, b) => b.score - a.score)
            .map((d, i) => ({ ...d, rank: i + 1 }));

          setRankingData(ranked);
          break;
        }
        case 'activity': {
          const arrays = await Promise.all(deviceIds.map(id => fetchTrips(id, from, to).catch(() => [] as Trip[])));
          setTrips(arrays.flat());
          break;
        }
        case 'events-chart': {
          const arrays = await Promise.all(deviceIds.map(id => fetchEvents(id, from, to).catch(() => [] as Event[])));
          setEvents(arrays.flat());
          break;
        }
        case 'driver-chart': {
          const allTripsArrays = await Promise.all(
            devices.map(d => fetchTrips(d.id, from, to).catch(() => [] as Trip[]))
          );
          const allTrips = allTripsArrays.flat();
          const accumulated = new Map<string, { driverName: string; trips: number; distance: number; speedSum: number; maxSpeed: number }>();
          for (const trip of allTrips) {
            const key = trip.driverUniqueId || '';
            const driverName = trip.driverName ||
              drivers.find(d => d.uniqueId === trip.driverUniqueId)?.name ||
              (trip.driverUniqueId ? `Unknown (${trip.driverUniqueId})` : t('reports.noDriver'));
            if (!accumulated.has(key)) accumulated.set(key, { driverName, trips: 0, distance: 0, speedSum: 0, maxSpeed: 0 });
            const e = accumulated.get(key)!;
            e.trips++;
            e.distance += trip.distance;
            e.speedSum += trip.averageSpeed;
            e.maxSpeed = Math.max(e.maxSpeed, trip.maxSpeed);
          }
          setDriverTrips(Array.from(accumulated.entries()).map(([driverUniqueId, e]) => ({
            driverUniqueId,
            driverName: e.driverName,
            trips: e.trips,
            distance: e.distance,
            duration: 0,
            averageSpeed: e.trips > 0 ? e.speedSum / e.trips : 0,
            maxSpeed: e.maxSpeed,
          })));
          break;
        }
      }
      setHasGenerated(true);
    } catch (error) {
      console.error('Failed to generate report:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [dateRange, reportType, selectedDeviceId, selectedDriverId, speedLimit, minIdleTime, devices, drivers, t]);

  const getDeviceName = (deviceId: number) => {
    return devices.find(d => d.id === deviceId)?.name || `Device ${deviceId}`;
  };

  const hasData = () => {
    switch (reportType) {
      case 'trips': return trips.length > 0;
      case 'summary': return summaries.length > 0;
      case 'stops': return stops.length > 0;
      case 'idle': return stops.length > 0;
      case 'events': return events.length > 0;
      case 'route': return positions.length > 0;
      case 'raw': return positions.length > 0;
      case 'driver': return driverTrips.length > 0;
      case 'speeding': return speedingEvents.length > 0;
      case 'ranking': return rankingData.length > 0;
      case 'activity': return trips.length > 0;
      case 'events-chart': return events.length > 0;
      case 'driver-chart': return driverTrips.length > 0;
    }
  };

  const exportToExcel = useCallback(() => {
    const wb = XLSX.utils.book_new();
    let ws: XLSX.WorkSheet;

    switch (reportType) {
      case 'trips': {
        const data = trips.map(trip => ({
          [t('devices.name')]: trip.deviceName,
          [t('reports.driver')]: trip.driverName || '',
          [t('history.start')]: format(new Date(trip.startTime), 'yyyy-MM-dd HH:mm'),
          [t('history.end')]: format(new Date(trip.endTime), 'yyyy-MM-dd HH:mm'),
          [t('history.distance')]: `${(trip.distance / 1000).toFixed(1)} km`,
          [t('history.duration')]: formatHours(trip.duration),
          [t('history.avgSpeed')]: `${Math.round(trip.averageSpeed * 1.852)} km/h`,
          [t('history.maxSpeed')]: `${Math.round(trip.maxSpeed * 1.852)} km/h`,
          [t('history.start') + ' ' + t('devices.address')]: trip.startAddress,
          [t('history.end') + ' ' + t('devices.address')]: trip.endAddress,
        }));
        ws = XLSX.utils.json_to_sheet(data);
        break;
      }
      case 'summary': {
        const data = summaries.map(s => ({
          [t('devices.name')]: s.deviceName,
          [t('history.distance')]: `${(s.distance / 1000).toFixed(1)} km`,
          [t('history.avgSpeed')]: `${Math.round(s.averageSpeed * 1.852)} km/h`,
          [t('history.maxSpeed')]: `${Math.round(s.maxSpeed * 1.852)} km/h`,
          [t('history.engineHours')]: formatHours(s.engineHours),
          [t('history.fuel')]: `${s.spentFuel.toFixed(1)} L`,
        }));
        ws = XLSX.utils.json_to_sheet(data);
        break;
      }
      case 'stops': {
        const data = stops.map(stop => ({
          [t('devices.name')]: stop.deviceName,
          [t('history.start')]: format(new Date(stop.startTime), 'yyyy-MM-dd HH:mm'),
          [t('history.end')]: format(new Date(stop.endTime), 'yyyy-MM-dd HH:mm'),
          [t('history.duration')]: formatHours(stop.duration),
          [t('devices.address')]: stop.address,
          [t('history.engineHours')]: formatHours(stop.engineHours),
        }));
        ws = XLSX.utils.json_to_sheet(data);
        break;
      }
      case 'idle': {
        const data = stops.map(stop => ({
          [t('devices.name')]: stop.deviceName,
          [t('reports.driver')]: stop.driverName || '',
          [t('history.start')]: format(new Date(stop.startTime), 'yyyy-MM-dd HH:mm'),
          [t('history.end')]: format(new Date(stop.endTime), 'yyyy-MM-dd HH:mm'),
          [t('reports.idleTime')]: formatHours(stop.engineHours),
          [t('history.duration')]: formatHours(stop.duration),
          [t('devices.address')]: stop.address,
        }));
        ws = XLSX.utils.json_to_sheet(data);
        break;
      }
      case 'events': {
        const data = events.map(ev => {
          const driverUniqueId = ev.attributes?.driverUniqueId as string | undefined;
          const driverName = driverUniqueId ? (drivers.find(d => d.uniqueId === driverUniqueId)?.name || driverUniqueId) : '';
          return {
            [t('devices.name')]: devices.find(d => d.id === ev.deviceId)?.name || `Device ${ev.deviceId}`,
            [t('reports.type')]: ev.type === 'alarm' && ev.attributes?.alarm
              ? `${t(`notifications.events.${ev.type}`, ev.type)} (${t(`notifications.alarms.${ev.attributes.alarm as string}`, ev.attributes.alarm as string)})`
              : t(`notifications.events.${ev.type}`, ev.type),
            [t('reports.driver')]: driverName,
            [t('reports.time')]: format(new Date(ev.eventTime), 'yyyy-MM-dd HH:mm'),
          };
        });
        ws = XLSX.utils.json_to_sheet(data);
        break;
      }
      case 'route': {
        const data = positions.map(pos => ({
          [t('reports.time')]: format(new Date(pos.fixTime), 'yyyy-MM-dd HH:mm:ss'),
          [t('reports.latitude')]: pos.latitude.toFixed(6),
          [t('reports.longitude')]: pos.longitude.toFixed(6),
          [t('devices.speed')]: `${Math.round(pos.speed * 1.852)} km/h`,
          [t('devices.address')]: pos.address || '',
        }));
        ws = XLSX.utils.json_to_sheet(data);
        break;
      }
      case 'raw': {
        // Collect all unique attribute keys
        const attrKeys = new Set<string>();
        positions.forEach(pos => {
          if (pos.attributes) Object.keys(pos.attributes).forEach(k => attrKeys.add(k));
        });
        const sortedAttrKeys = Array.from(attrKeys).sort();

        const data = positions.map(pos => {
          const row: Record<string, unknown> = {
            'ID': pos.id,
            [t('reports.time')]: format(new Date(pos.fixTime), 'yyyy-MM-dd HH:mm:ss'),
            'Device Time': format(new Date(pos.deviceTime), 'yyyy-MM-dd HH:mm:ss'),
            'Server Time': format(new Date(pos.serverTime), 'yyyy-MM-dd HH:mm:ss'),
            'Delay (s)': Math.round((new Date(pos.serverTime).getTime() - new Date(pos.fixTime).getTime()) / 1000),
            'Protocol': pos.protocol,
            'Valid': pos.valid ? 'Yes' : 'No',
            [t('reports.latitude')]: pos.latitude,
            [t('reports.longitude')]: pos.longitude,
            'Altitude': pos.altitude,
            [t('devices.speed') + ' (km/h)']: Math.round(pos.speed * 1.852),
            'Course': pos.course,
            [t('devices.address')]: pos.address || '',
          };
          sortedAttrKeys.forEach(key => {
            row[key] = pos.attributes?.[key] ?? '';
          });
          return row;
        });
        ws = XLSX.utils.json_to_sheet(data);
        break;
      }
      case 'driver': {
        const data = driverTrips.map(dr => ({
          [t('reports.driverName')]: dr.driverName,
          [t('reports.tripCount')]: dr.trips,
          [t('history.distance')]: `${(dr.distance / 1000).toFixed(1)} km`,
          [t('history.duration')]: formatHours(dr.duration),
          [t('history.avgSpeed')]: `${Math.round(dr.averageSpeed * 1.852)} km/h`,
          [t('history.maxSpeed')]: `${Math.round(dr.maxSpeed * 1.852)} km/h`,
        }));
        ws = XLSX.utils.json_to_sheet(data);
        break;
      }
      case 'speeding': {
        const data = speedingEvents.map(ev => ({
          [t('devices.name')]: ev.deviceName,
          [t('reports.driverName')]: ev.driverName || '',
          [t('history.start')]: format(new Date(ev.startTime), 'yyyy-MM-dd HH:mm:ss'),
          [t('history.end')]: format(new Date(ev.endTime), 'yyyy-MM-dd HH:mm:ss'),
          [t('history.duration')]: formatHours(ev.duration),
          [t('history.maxSpeed')]: `${Math.round(ev.maxSpeed * 1.852)} km/h`,
          [t('history.avgSpeed')]: `${Math.round(ev.avgSpeed * 1.852)} km/h`,
          [t('reports.speedLimit')]: `${speedLimit} km/h`,
          [t('devices.address')]: ev.address,
        }));
        ws = XLSX.utils.json_to_sheet(data);
        break;
      }
      case 'ranking': {
        const data = rankingData.map(dr => ({
          '#': dr.rank,
          [t('reports.driverName')]: dr.driverName,
          [t('reports.tripCount')]: dr.trips,
          [t('history.distance')]: `${(dr.distance / 1000).toFixed(1)} km`,
          [t('history.duration')]: formatHours(dr.duration),
          [t('history.avgSpeed')]: `${Math.round(dr.averageSpeed * 1.852)} km/h`,
          [t('history.maxSpeed')]: `${Math.round(dr.maxSpeed * 1.852)} km/h`,
          [t('notifications.alarms.hardBraking')]: dr.harshBraking,
          [t('notifications.alarms.hardAcceleration')]: dr.harshAcceleration,
          [t('notifications.alarms.hardCornering')]: dr.harshCornering,
          [t('reports.score')]: dr.score,
        }));
        ws = XLSX.utils.json_to_sheet(data);
        break;
      }
    }

    XLSX.utils.book_append_sheet(wb, ws!, t(`reports.reportTypes.${reportType}`));
    XLSX.writeFile(wb, `report_${reportType}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  }, [reportType, trips, summaries, stops, events, positions, driverTrips, speedingEvents, rankingData, speedLimit, devices, drivers, formatHours, t]);

  // Chart data computations (must be before any early returns)
  const activityChartData = useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) return [];
    const days = eachDayOfInterval({ start: dateRange.from, end: dateRange.to });
    return days.map(day => {
      const dayStr = format(day, 'MMM d');
      const dayTrips = trips.filter(t => format(new Date(t.startTime), 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd'));
      return {
        date: dayStr,
        distance: parseFloat((dayTrips.reduce((s, t) => s + t.distance, 0) / 1000).toFixed(1)),
        trips: dayTrips.length,
      };
    });
  }, [trips, dateRange]);

  const eventsChartData = useMemo(() => {
    const counts = new Map<string, number>();
    for (const ev of events) {
      counts.set(ev.type, (counts.get(ev.type) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count);
  }, [events]);

  const driverChartData = useMemo(() =>
    [...driverTrips]
      .sort((a, b) => b.distance - a.distance)
      .slice(0, 15)
      .map(d => ({
        name: d.driverName.length > 16 ? d.driverName.slice(0, 15) + '…' : d.driverName,
        fullName: d.driverName,
        distance: parseFloat((d.distance / 1000).toFixed(1)),
        trips: d.trips,
        avgSpeed: Math.round(d.averageSpeed * 1.852),
      }))
  , [driverTrips]);

  if (isLoading && !hasLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const needsDevice = reportType !== 'driver' && reportType !== 'ranking' && reportType !== 'driver-chart';
  const canGenerate = dateRange?.from && dateRange?.to && (!needsDevice || selectedDeviceId !== '');

  const headerContent = (
    <div className="flex items-center gap-2 flex-nowrap">
      <Select value={reportType} onValueChange={(v) => { setReportType(v as ReportType); setHasGenerated(false); clearResults(); }}>
        <SelectTrigger className="w-[120px] xl:w-[160px]">
          <SelectValue placeholder={t('reports.selectReportType')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="trips">{t('reports.reportTypes.trips')}</SelectItem>
          <SelectItem value="events">{t('reports.reportTypes.events')}</SelectItem>
          <SelectItem value="stops">{t('reports.reportTypes.stops')}</SelectItem>
          <SelectItem value="summary">{t('reports.reportTypes.summary')}</SelectItem>
          <SelectItem value="route">{t('reports.reportTypes.route')}</SelectItem>
          <SelectItem value="raw">{t('reports.reportTypes.raw')}</SelectItem>
          <SelectItem value="driver">{t('reports.reportTypes.driver')}</SelectItem>
          <SelectItem value="speeding">{t('reports.reportTypes.speeding')}</SelectItem>
          <SelectItem value="idle">{t('reports.reportTypes.idle')}</SelectItem>
          <SelectItem value="ranking">{t('reports.reportTypes.ranking')}</SelectItem>
          <SelectItem value="activity">{t('reports.reportTypes.activity')}</SelectItem>
          <SelectItem value="events-chart">{t('reports.reportTypes.eventsChart')}</SelectItem>
          <SelectItem value="driver-chart">{t('reports.reportTypes.driverChart')}</SelectItem>
        </SelectContent>
      </Select>

      {reportType === 'speeding' && (
        <Select value={speedLimit.toString()} onValueChange={(v) => setSpeedLimit(Number(v))}>
          <SelectTrigger className="w-[100px] @2xl:w-[130px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[50, 60, 70, 80, 90, 100, 110, 120, 130].map(v => (
              <SelectItem key={v} value={v.toString()}>{v} km/h</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {reportType === 'idle' && (
        <Select value={minIdleTime.toString()} onValueChange={(v) => setMinIdleTime(Number(v))}>
          <SelectTrigger className="w-[110px] @2xl:w-[150px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[5, 10, 15, 20, 30, 45, 60].map(v => (
              <SelectItem key={v} value={v.toString()}>{t('reports.minIdleTime', { min: v })}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {reportType === 'driver' && (
        <Select value={selectedDriverId} onValueChange={setSelectedDriverId}>
          <SelectTrigger className="w-[140px] @2xl:w-[200px]">
            <SelectValue placeholder={t('reports.selectDriver')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            {drivers.map((driver) => (
              <SelectItem key={driver.id} value={driver.uniqueId}>
                {driver.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {reportType !== 'driver' && reportType !== 'ranking' && reportType !== 'driver-chart' && (
        <Popover open={devicePickerOpen} onOpenChange={setDevicePickerOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" role="combobox" aria-expanded={devicePickerOpen} className="w-[140px] @2xl:w-[200px] justify-between font-normal">
              <span className="truncate">
                {selectedDeviceId === 'all'
                  ? t('common.all')
                  : (devices.find(d => d.id.toString() === selectedDeviceId)?.name ?? t('history.selectDevice'))}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[220px] p-0" align="start">
            <Command>
              <CommandInput placeholder={t('devices.searchPlaceholder')} className="h-9" />
              <CommandList>
                <CommandEmpty>{t('devices.noDevicesMatch')}</CommandEmpty>
                <CommandGroup>
                  <CommandItem value="all" onSelect={() => { setSelectedDeviceId('all'); setDevicePickerOpen(false); }}>
                    <Check className={cn('mr-2 h-4 w-4', selectedDeviceId === 'all' ? 'opacity-100' : 'opacity-0')} />
                    {t('common.all')}
                  </CommandItem>
                  {devices.map((device) => (
                    <CommandItem
                      key={device.id}
                      value={device.name}
                      onSelect={() => { setSelectedDeviceId(device.id.toString()); setDevicePickerOpen(false); }}
                    >
                      <Check className={cn('mr-2 h-4 w-4', selectedDeviceId === device.id.toString() ? 'opacity-100' : 'opacity-0')} />
                      {device.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}

      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="gap-2 shrink-0">
            <Calendar className="h-4 w-4" />
            {dateRange?.from ? (
              dateRange.to ? (
                <>
                  <span className="@2xl:hidden">{format(dateRange.from, "d/M")} - {format(dateRange.to, "d/M")}</span>
                  <span className="hidden @2xl:inline">{format(dateRange.from, "MMM d", { locale: dateFnsLocale })} - {format(dateRange.to, "MMM d, yyyy", { locale: dateFnsLocale })}</span>
                </>
              ) : (
                <>
                  <span className="@2xl:hidden">{format(dateRange.from, "d/M/yy")}</span>
                  <span className="hidden @2xl:inline">{format(dateRange.from, "MMM d, yyyy", { locale: dateFnsLocale })}</span>
                </>
              )
            ) : (
              t('history.selectDates')
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <CalendarComponent
            mode="range"
            selected={dateRange}
            onSelect={(range) => setDateRange(range ? {
              from: range.from ? startOfDay(range.from) : undefined,
              to: range.to ? endOfDay(range.to) : (range.from ? endOfDay(range.from) : undefined),
            } : undefined)}
            numberOfMonths={2}
            disabled={(date) => date > new Date()}
          />
        </PopoverContent>
      </Popover>

      <Button onClick={generateReport} disabled={!canGenerate || isGenerating} className="gap-2">
        {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
        <span className="hidden xl:inline">{t('reports.generate')}</span>
      </Button>

      {hasGenerated && hasData() && (
        <>
          <Button variant="outline" onClick={exportToExcel} className="gap-2 shrink-0">
            <Download className="h-4 w-4" />
            <span className="hidden xl:inline">{t('reports.export')}</span>
          </Button>
          <Button variant="outline" onClick={() => window.print()} className="gap-2 shrink-0">
            <Printer className="h-4 w-4" />
            <span className="hidden xl:inline">{t('reports.print')}</span>
          </Button>
        </>
      )}
    </div>
  );

  const renderTable = () => {
    if (!hasGenerated) return null;

    if (!hasData()) {
      return (
        <div className="text-center py-12 text-muted-foreground">
          <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>{t('reports.noData')}</p>
        </div>
      );
    }

    switch (reportType) {
      case 'trips':
        return (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('devices.name')}</TableHead>
                <TableHead>{t('reports.driver')}</TableHead>
                <TableHead>{t('history.start')}</TableHead>
                <TableHead>{t('history.end')}</TableHead>
                <TableHead>{t('history.startAddress')}</TableHead>
                <TableHead>{t('history.endAddress')}</TableHead>
                <TableHead className="text-right">{t('history.distance')}</TableHead>
                <TableHead className="text-right">{t('history.duration')}</TableHead>
                <TableHead className="text-right">{t('history.avgSpeed')}</TableHead>
                <TableHead className="text-right">{t('history.maxSpeed')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trips.map((trip, i) => (
                <TableRow key={i}>
                  <TableCell className="font-medium">{trip.deviceName}</TableCell>
                  <TableCell>{trip.driverName || '—'}</TableCell>
                  <TableCell title={format(new Date(trip.startTime), 'yyyy-MM-dd HH:mm:ss')}>{format(new Date(trip.startTime), 'MMM d, HH:mm', { locale: dateFnsLocale })}</TableCell>
                  <TableCell title={format(new Date(trip.endTime), 'yyyy-MM-dd HH:mm:ss')}>{format(new Date(trip.endTime), 'MMM d, HH:mm', { locale: dateFnsLocale })}</TableCell>
                  <TableCell className="max-w-[200px]"><TwoLineAddress address={trip.startAddress} /></TableCell>
                  <TableCell className="max-w-[200px]"><TwoLineAddress address={trip.endAddress} /></TableCell>
                  <TableCell className="text-right">{(trip.distance / 1000).toFixed(1)} km</TableCell>
                  <TableCell className="text-right">{formatHours(trip.duration)}</TableCell>
                  <TableCell className="text-right">{Math.round(trip.averageSpeed * 1.852)} km/h</TableCell>
                  <TableCell className="text-right">{Math.round(trip.maxSpeed * 1.852)} km/h</TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow className="font-semibold">
                <TableCell colSpan={6}>{t('reports.total')} ({trips.length})</TableCell>
                <TableCell className="text-right">{(trips.reduce((sum, t) => sum + t.distance, 0) / 1000).toFixed(1)} km</TableCell>
                <TableCell className="text-right">{formatHours(trips.reduce((sum, t) => sum + t.duration, 0))}</TableCell>
                <TableCell className="text-right">
                  {trips.length > 0 ? Math.round(trips.reduce((sum, t) => sum + t.averageSpeed, 0) / trips.length * 1.852) : 0} km/h
                </TableCell>
                <TableCell className="text-right">{Math.round(Math.max(...trips.map(t => t.maxSpeed)) * 1.852)} km/h</TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        );

      case 'summary':
        return (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('devices.name')}</TableHead>
                <TableHead className="text-right">{t('history.distance')}</TableHead>
                <TableHead className="text-right">{t('history.avgSpeed')}</TableHead>
                <TableHead className="text-right">{t('history.maxSpeed')}</TableHead>
                <TableHead className="text-right">{t('history.engineHours')}</TableHead>
                <TableHead className="text-right">{t('history.fuel')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {summaries.map((s) => (
                <TableRow key={s.deviceId}>
                  <TableCell className="font-medium">{s.deviceName}</TableCell>
                  <TableCell className="text-right">{(s.distance / 1000).toFixed(1)} km</TableCell>
                  <TableCell className="text-right">{Math.round(s.averageSpeed * 1.852)} km/h</TableCell>
                  <TableCell className="text-right">{Math.round(s.maxSpeed * 1.852)} km/h</TableCell>
                  <TableCell className="text-right">{formatHours(s.engineHours)}</TableCell>
                  <TableCell className="text-right">{s.spentFuel.toFixed(1)} L</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );

      case 'stops':
        return (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('devices.name')}</TableHead>
                <TableHead>{t('history.start')}</TableHead>
                <TableHead>{t('history.end')}</TableHead>
                <TableHead className="text-right">{t('history.duration')}</TableHead>
                <TableHead>{t('devices.address')}</TableHead>
                <TableHead className="text-right">{t('history.engineHours')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stops.map((stop, i) => (
                <TableRow key={i}>
                  <TableCell className="font-medium">{stop.deviceName}</TableCell>
                  <TableCell>{format(new Date(stop.startTime), 'MMM d, HH:mm', { locale: dateFnsLocale })}</TableCell>
                  <TableCell>{format(new Date(stop.endTime), 'MMM d, HH:mm', { locale: dateFnsLocale })}</TableCell>
                  <TableCell className="text-right">{formatHours(stop.duration)}</TableCell>
                  <TableCell className="max-w-[200px]"><TwoLineAddress address={stop.address} /></TableCell>
                  <TableCell className="text-right">{formatHours(stop.engineHours)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );

      case 'idle':
        return (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('devices.name')}</TableHead>
                <TableHead>{t('reports.driver')}</TableHead>
                <TableHead>{t('history.start')}</TableHead>
                <TableHead>{t('history.end')}</TableHead>
                <TableHead className="text-right">{t('reports.idleTime')}</TableHead>
                <TableHead className="text-right">{t('history.duration')}</TableHead>
                <TableHead>{t('devices.address')}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stops.map((stop, i) => (
                <TableRow key={i}>
                  <TableCell className="font-medium">{stop.deviceName}</TableCell>
                  <TableCell>{stop.driverName || '—'}</TableCell>
                  <TableCell>{format(new Date(stop.startTime), 'MMM d, HH:mm', { locale: dateFnsLocale })}</TableCell>
                  <TableCell>{format(new Date(stop.endTime), 'MMM d, HH:mm', { locale: dateFnsLocale })}</TableCell>
                  <TableCell className="text-right font-medium text-amber-600">{formatHours(stop.engineHours)}</TableCell>
                  <TableCell className="text-right">{formatHours(stop.duration)}</TableCell>
                  <TableCell className="max-w-[200px]"><TwoLineAddress address={stop.address} /></TableCell>
                  <TableCell>
                    <a
                      href={`/history/trips?deviceId=${stop.deviceId}&date=${format(new Date(stop.startTime), 'yyyy-MM-dd')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline whitespace-nowrap text-sm"
                    >
                      Route
                    </a>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );

      case 'events': {
        const eventTypes = Array.from(new Set(events.map(ev => ev.type))).sort();
        const filteredEvents = eventTypeFilter.size === 0 ? events : events.filter(ev => eventTypeFilter.has(ev.type));
        const toggleEventType = (type: string) => {
          setEventTypeFilter(prev => {
            const next = new Set(prev);
            if (next.has(type)) next.delete(type); else next.add(type);
            return next;
          });
        };
        return (
          <div className="space-y-3">
            {eventTypes.length > 1 && (
              <div className="flex flex-wrap gap-2 px-1">
                {eventTypes.map(type => (
                  <Badge
                    key={type}
                    variant={eventTypeFilter.has(type) ? 'default' : 'outline'}
                    className="cursor-pointer select-none"
                    onClick={() => toggleEventType(type)}
                  >
                    {t(`notifications.events.${type}`, type)}
                  </Badge>
                ))}
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('devices.name')}</TableHead>
                  <TableHead>{t('reports.type')}</TableHead>
                  <TableHead>{t('reports.driver')}</TableHead>
                  <TableHead>{t('reports.time')}</TableHead>
                  <TableHead>{t('devices.address')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEvents.map((ev) => {
                  const driverUniqueId = ev.attributes?.driverUniqueId as string | undefined;
                  const driverName = driverUniqueId
                    ? (drivers.find(d => d.uniqueId === driverUniqueId)?.name || driverUniqueId)
                    : undefined;
                  const pos = eventPositions.get(ev.positionId);
                  const activityLink = `/history/trips?deviceId=${ev.deviceId}&date=${format(new Date(ev.eventTime), 'yyyy-MM-dd')}${pos?.fixTime ? `&time=${new Date(pos.fixTime).getTime()}` : ''}`;
                  return (
                    <TableRow key={ev.id}>
                      <TableCell className="font-medium">{getDeviceName(ev.deviceId)}</TableCell>
                      <TableCell>
                        {t(`notifications.events.${ev.type}`, ev.type)}
                        {ev.type === 'alarm' && ev.attributes?.alarm && (
                          <span className="ml-1 text-muted-foreground">
                            ({t(`notifications.alarms.${ev.attributes.alarm as string}`, ev.attributes.alarm as string)})
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{driverName || '—'}</TableCell>
                      <TableCell>{format(new Date(ev.eventTime), 'MMM d, HH:mm', { locale: dateFnsLocale })}</TableCell>
                      <TableCell className="max-w-[220px]">
                        {pos?.address ? (
                          <a href={activityLink} className="text-primary hover:underline truncate block" title={pos.address}>
                            {pos.address}
                          </a>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        );
      }

      case 'route':
        return (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('reports.time')}</TableHead>
                <TableHead className="text-right">{t('reports.latitude')}</TableHead>
                <TableHead className="text-right">{t('reports.longitude')}</TableHead>
                <TableHead className="text-right">{t('devices.speed')}</TableHead>
                <TableHead className="text-right">{t('devices.odometer')}</TableHead>
                <TableHead className="text-right">{t('devices.engineHours')}</TableHead>
                <TableHead>{t('devices.address')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {positions.map((pos) => (
                <TableRow key={pos.id}>
                  <TableCell>{format(new Date(pos.fixTime), 'MMM d, HH:mm:ss', { locale: dateFnsLocale })}</TableCell>
                  <TableCell className="text-right">{pos.latitude.toFixed(6)}</TableCell>
                  <TableCell className="text-right">{pos.longitude.toFixed(6)}</TableCell>
                  <TableCell className="text-right">{Math.round(pos.speed * 1.852)} km/h</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {pos.attributes?.totalDistance != null
                      ? `${((pos.attributes.totalDistance as number) / 1000).toLocaleString(undefined, { maximumFractionDigits: 0 })} km`
                      : '—'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {pos.attributes?.hours != null
                      ? `${((pos.attributes.hours as number) / 3600000).toLocaleString(undefined, { maximumFractionDigits: 1 })} h`
                      : '—'}
                  </TableCell>
                  <TableCell className="max-w-[200px]"><TwoLineAddress address={pos.address} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );

      case 'driver':
        return (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('reports.driverName')}</TableHead>
                <TableHead className="text-right">{t('reports.tripCount')}</TableHead>
                <TableHead className="text-right">{t('history.distance')}</TableHead>
                <TableHead className="text-right">{t('history.duration')}</TableHead>
                <TableHead className="text-right">{t('history.avgSpeed')}</TableHead>
                <TableHead className="text-right">{t('history.maxSpeed')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {driverTrips.map((dr) => (
                <TableRow key={dr.driverUniqueId || 'none'}>
                  <TableCell className="font-medium">{dr.driverName}</TableCell>
                  <TableCell className="text-right">{dr.trips}</TableCell>
                  <TableCell className="text-right">{(dr.distance / 1000).toFixed(1)} km</TableCell>
                  <TableCell className="text-right">{formatHours(dr.duration)}</TableCell>
                  <TableCell className="text-right">{Math.round(dr.averageSpeed * 1.852)} km/h</TableCell>
                  <TableCell className="text-right">{Math.round(dr.maxSpeed * 1.852)} km/h</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );

      case 'speeding':
        return (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('devices.name')}</TableHead>
                <TableHead>{t('reports.driverName')}</TableHead>
                <TableHead>{t('history.start')}</TableHead>
                <TableHead>{t('history.end')}</TableHead>
                <TableHead className="text-right">{t('history.duration')}</TableHead>
                <TableHead className="text-right">{t('history.maxSpeed')}</TableHead>
                <TableHead className="text-right">{t('history.avgSpeed')}</TableHead>
                <TableHead>{t('devices.address')}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {speedingEvents.map((ev, i) => (
                <TableRow key={i}>
                  <TableCell className="font-medium">{ev.deviceName}</TableCell>
                  <TableCell>{ev.driverName || '—'}</TableCell>
                  <TableCell className="whitespace-nowrap">{format(new Date(ev.startTime), 'MMM d, HH:mm:ss', { locale: dateFnsLocale })}</TableCell>
                  <TableCell className="whitespace-nowrap">{format(new Date(ev.endTime), 'MMM d, HH:mm:ss', { locale: dateFnsLocale })}</TableCell>
                  <TableCell className="text-right">{formatHours(ev.duration)}</TableCell>
                  <TableCell className="text-right font-medium text-destructive">{Math.round(ev.maxSpeed * 1.852)} km/h</TableCell>
                  <TableCell className="text-right">{Math.round(ev.avgSpeed * 1.852)} km/h</TableCell>
                  <TableCell className="max-w-[240px]"><TwoLineAddress address={ev.address} /></TableCell>
                  <TableCell>
                    <a
                      href={`https://www.google.com/maps?q=${ev.latitude},${ev.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline whitespace-nowrap text-sm"
                    >
                      📍 Map
                    </a>
                    <div className="text-xs text-muted-foreground mt-0.5 whitespace-nowrap">
                      {format(new Date(ev.fixTime), 'HH:mm:ss')} · {Math.round(ev.startSpeed * 1.852)} km/h
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );

      case 'ranking': {
        const rankColor = (rank: number) =>
          rank === 1 ? 'text-yellow-500' : rank === 2 ? 'text-slate-400' : rank === 3 ? 'text-amber-600' : 'text-muted-foreground';
        const scoreColor = (score: number) =>
          score >= 80 ? 'text-green-600' : score >= 60 ? 'text-yellow-600' : 'text-destructive';

        return (
          <div className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>{t('reports.driverName')}</TableHead>
                  <TableHead className="text-right">{t('reports.tripCount')}</TableHead>
                  <TableHead className="text-right">{t('history.distance')}</TableHead>
                  <TableHead className="text-right">{t('history.duration')}</TableHead>
                  <TableHead className="text-right">{t('history.avgSpeed')}</TableHead>
                  <TableHead className="text-right">{t('history.maxSpeed')}</TableHead>
                  <TableHead className="text-right" title={t('notifications.alarms.hardBraking')}>🛑</TableHead>
                  <TableHead className="text-right" title={t('notifications.alarms.hardAcceleration')}>⚡</TableHead>
                  <TableHead className="text-right" title={t('notifications.alarms.hardCornering')}>↪</TableHead>
                  <TableHead className="text-right">{t('reports.score')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rankingData.map((dr) => (
                  <TableRow key={dr.driverUniqueId || 'none'}>
                    <TableCell className={`font-bold text-base ${rankColor(dr.rank)}`}>{dr.rank}</TableCell>
                    <TableCell className="font-medium">{dr.driverName}</TableCell>
                    <TableCell className="text-right">{dr.trips}</TableCell>
                    <TableCell className="text-right">{(dr.distance / 1000).toFixed(1)} km</TableCell>
                    <TableCell className="text-right">{formatHours(dr.duration)}</TableCell>
                    <TableCell className="text-right">{Math.round(dr.averageSpeed * 1.852)} km/h</TableCell>
                    <TableCell className="text-right">{Math.round(dr.maxSpeed * 1.852)} km/h</TableCell>
                    <TableCell className={`text-right ${dr.harshBraking > 0 ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>{dr.harshBraking}</TableCell>
                    <TableCell className={`text-right ${dr.harshAcceleration > 0 ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>{dr.harshAcceleration}</TableCell>
                    <TableCell className={`text-right ${dr.harshCornering > 0 ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>{dr.harshCornering}</TableCell>
                    <TableCell className={`text-right font-bold ${scoreColor(dr.score)}`}>{dr.score}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <p className="text-xs text-muted-foreground px-1">{t('reports.rankingScoreNote')}</p>
          </div>
        );
      }

      case 'raw': {
        // Collect all unique attribute keys across all positions
        const attrKeys = new Set<string>();
        positions.forEach(pos => {
          if (pos.attributes) Object.keys(pos.attributes).forEach(k => attrKeys.add(k));
        });
        const sortedAttrKeys = Array.from(attrKeys).sort();
        const totalPages = Math.ceil(positions.length / RAW_PAGE_SIZE);
        const pagePositions = positions.slice(rawPage * RAW_PAGE_SIZE, (rawPage + 1) * RAW_PAGE_SIZE);
        const thClass = "sticky top-0 bg-muted h-10 px-4 text-left align-middle text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap border-b border-border";
        const thRClass = "sticky top-0 bg-muted h-10 px-4 text-right align-middle text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap border-b border-border";

        return (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm text-muted-foreground px-1">
              <span>{positions.length.toLocaleString()} rows{totalPages > 1 ? ` · page ${rawPage + 1} of ${totalPages}` : ''}</span>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setRawPage(p => Math.max(0, p - 1))} disabled={rawPage === 0}>←</Button>
                  <Button variant="outline" size="sm" onClick={() => setRawPage(p => Math.min(totalPages - 1, p + 1))} disabled={rawPage === totalPages - 1}>→</Button>
                </div>
              )}
            </div>
          <div className="overflow-x-auto">
          <div className="overflow-y-auto max-h-[calc(100vh-150px)]">
            <table className="w-full caption-bottom text-sm">
              <thead>
                <tr>
                  <th className={thClass}>ID</th>
                  <th className={thClass}>{t('reports.time')}</th>
                  <th className={thClass}>Server Time</th>
                  <th className={thRClass}>Delay</th>
                  <th className={thClass}>Protocol</th>
                  <th className={thClass}>Valid</th>
                  <th className={thRClass}>{t('reports.latitude')}</th>
                  <th className={thRClass}>{t('reports.longitude')}</th>
                  <th className={thRClass}>Altitude</th>
                  <th className={thRClass}>{t('devices.speed')}</th>
                  <th className={thRClass}>Course</th>
                  <th className={thClass}>{t('devices.address')}</th>
                  {sortedAttrKeys.map(key => (
                    <th key={key} className={thRClass}>{key}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="[&_tr:last-child]:border-0">
                {pagePositions.map((pos) => (
                  <tr key={pos.id} className="border-b transition-colors hover:bg-muted/50">
                    <td className="p-2 sm:p-4 align-middle text-muted-foreground font-mono text-xs">{pos.id}</td>
                    <td className="p-2 sm:p-4 align-middle whitespace-nowrap">{format(new Date(pos.fixTime), 'MMM d, HH:mm:ss', { locale: dateFnsLocale })}</td>
                    <td className="p-2 sm:p-4 align-middle whitespace-nowrap text-muted-foreground">{format(new Date(pos.serverTime), 'MMM d, HH:mm:ss', { locale: dateFnsLocale })}</td>
                    <td className="p-2 sm:p-4 align-middle text-right whitespace-nowrap">{(() => { const diff = Math.round((new Date(pos.serverTime).getTime() - new Date(pos.fixTime).getTime()) / 1000); return diff >= 60 ? `${Math.floor(diff / 60)}m ${diff % 60}s` : `${diff}s`; })()}</td>
                    <td className="p-2 sm:p-4 align-middle">{pos.protocol}</td>
                    <td className="p-2 sm:p-4 align-middle">{pos.valid ? 'Yes' : 'No'}</td>
                    <td className="p-2 sm:p-4 align-middle text-right">{pos.latitude.toFixed(6)}</td>
                    <td className="p-2 sm:p-4 align-middle text-right">{pos.longitude.toFixed(6)}</td>
                    <td className="p-2 sm:p-4 align-middle text-right">{pos.altitude.toFixed(1)}</td>
                    <td className="p-2 sm:p-4 align-middle text-right">{Math.round(pos.speed * 1.852)} km/h</td>
                    <td className="p-2 sm:p-4 align-middle text-right">{pos.course.toFixed(0)}°</td>
                    <td className="p-2 sm:p-4 align-middle max-w-[200px] truncate">{pos.address || ''}</td>
                    {sortedAttrKeys.map(key => (
                      <td key={key} className="p-2 sm:p-4 align-middle text-right whitespace-nowrap">
                        {pos.attributes?.[key] != null ? String(pos.attributes[key]) : ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
          </div>
        );
      }

      case 'activity':
        return (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground px-1">{t('reports.charts.activityNote')}</p>
            <ResponsiveContainer width="100%" height={360}>
              <ComposedChart data={activityChartData} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="dist" orientation="left" tick={{ fontSize: 11 }} unit=" km" />
                <YAxis yAxisId="trips" orientation="right" allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(val, name) => name === t('history.distance') ? `${val} km` : val} />
                <Legend />
                <Bar yAxisId="dist" dataKey="distance" name={t('history.distance')} fill={CHART_COLORS[0]} radius={[3,3,0,0]} />
                <Line yAxisId="trips" type="monotone" dataKey="trips" name={t('reports.tripCount')} stroke={CHART_COLORS[1]} strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        );

      case 'events-chart': {
        const topTypes = eventsChartData.slice(0, 12);
        return (
          <div className="space-y-2">
            <ResponsiveContainer width="100%" height={Math.max(260, topTypes.length * 36)}>
              <BarChart data={topTypes} layout="vertical" margin={{ top: 0, right: 24, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="type"
                  tickFormatter={(v) => {
                    const key = `notifications.events.${v}`;
                    const tr = t(key, { defaultValue: v });
                    return tr.length > 22 ? tr.slice(0, 21) + '…' : tr;
                  }}
                  width={150} tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(val) => [val, t('reports.charts.count')]}
                  labelFormatter={(v) => t(`notifications.events.${v}`, { defaultValue: v })}
                />
                <Bar dataKey="count" name={t('reports.charts.count')} radius={[0,3,3,0]}>
                  {topTypes.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        );
      }

      case 'driver-chart':
        return (
          <div className="space-y-6">
            <div>
              <p className="text-xs text-muted-foreground mb-2 px-1">{t('history.distance')} (km)</p>
              <ResponsiveContainer width="100%" height={Math.max(260, driverChartData.length * 36)}>
                <BarChart data={driverChartData} layout="vertical" margin={{ top: 0, right: 24, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} unit=" km" />
                  <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(val) => [`${val} km`, t('history.distance')]} labelFormatter={(_, p) => p[0]?.payload?.fullName ?? ''} />
                  <Bar dataKey="distance" name={t('history.distance')} radius={[0,3,3,0]}>
                    {driverChartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-2 px-1">{t('reports.tripCount')}</p>
              <ResponsiveContainer width="100%" height={Math.max(260, driverChartData.length * 36)}>
                <BarChart data={driverChartData} layout="vertical" margin={{ top: 0, right: 24, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(val) => [val, t('reports.tripCount')]} labelFormatter={(_, p) => p[0]?.payload?.fullName ?? ''} />
                  <Bar dataKey="trips" name={t('reports.tripCount')} fill={CHART_COLORS[2]} radius={[0,3,3,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
    }
  };

  return (
    <DashboardLayout headerContent={headerContent}>
      <div className="p-6">
        {/* Print-only title with report info */}
        <div className="print-title hidden mb-4">
          <div className="print-title-header">
            <img src={brandLogo || '/favicon.svg'} alt={brandTitle} className="print-logo" />
            <div>
              <h1 className="text-xl font-bold">{t('reports.title')} - {t(`reports.reportTypes.${reportType}`)}</h1>
              {selectedDeviceId !== 'all' && (
                <p className="text-sm text-muted-foreground">{devices.find(d => d.id.toString() === selectedDeviceId)?.name}</p>
              )}
              {dateRange?.from && dateRange?.to && (
                <p className="text-sm text-muted-foreground">
                  {format(dateRange.from, 'MMM d, yyyy', { locale: dateFnsLocale })} — {format(dateRange.to, 'MMM d, yyyy', { locale: dateFnsLocale })}
                </p>
              )}
            </div>
          </div>
        </div>
        {isGenerating ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          renderTable()
        )}
      </div>
    </DashboardLayout>
  );
};

export default Reports;
